'use client';

import Modal from '@/components/shared/modal';
import { ProductSizeGuide } from '@/types/product_page/productType';
import React, { useCallback, useEffect, useState } from 'react';
import { useSizeModal } from '@/helpers/size_guide_modal';
import { Option, Variant } from '@/types/product_page/productType';
import { Ruler } from '@phosphor-icons/react';
import VariantButton from '@/components/ui/VariantButton';

interface SelectedOptions {
  [key: string]: string | undefined;
}

interface IOptions {
  variants: Variant[];
  options: Option[];
  onSelect: (key: string, value: string) => void;
  selectedOptions: SelectedOptions;
  sizeModal?: ProductSizeGuide;
  variantOrders?: {
    variant_title: string;
    sort: number;
  }[];
  slug: string;
}

const Options: React.FC<IOptions> = ({
  variants,
  options,
  onSelect,
  sizeModal,
  selectedOptions,
  variantOrders,
  slug,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toggleSizeModal } = useSizeModal();

  const getAllAvailableOptions = useCallback(() => {
    const availableOptions: { [key: string]: Set<string> } = {};

    // Create a map for sorting based on the `variants` array
    const variantsSortMap = new Map(
      variantOrders
        ?.filter((v) => v?.variant_title) // Ensure `variant_title` is valid
        .map((v) => [v.variant_title.toLowerCase(), v.sort])
    );

    variants?.forEach((variant) => {
      variant?.option_values.forEach((optionValue) => {
        const option = options?.find((opt) => opt.id == optionValue.option);
        if (option) {
          if (!availableOptions[option.title]) {
            availableOptions[option.title] = new Set();
          }
          availableOptions[option.title].add(optionValue.value);
        }
      });
    });

    // Sort the sets in `availableOptions` based on the sort order from `variantsSortMap`
    Object.keys(availableOptions).forEach((key) => {
      const sortedValues = Array.from(availableOptions[key]).sort((a, b) => {
        const sortA = variantsSortMap.get(a.trim().toLowerCase()) || Infinity;
        const sortB = variantsSortMap.get(b.trim().toLowerCase()) || Infinity;

        return sortA - sortB; // Sort by the `sort` number
      });

      // Replace the original set with a new sorted set
      availableOptions[key] = new Set(sortedValues);
    });

    return availableOptions;
  }, [variants, options, variantOrders]);

  const availableOptions = getAllAvailableOptions();
  const isOptionCombinationAvailable = useCallback(
    (currentOptionTitle: string, currentValue: string) => {
      const possibleSelections = {
        ...selectedOptions,
        [currentOptionTitle]: currentValue,
      };

      return variants.some((variant) =>
        variant.option_values.every((optionValue) => {
          const optionTitle = options.find(
            (option) => option.id == optionValue.option
          )?.title;

          return possibleSelections[optionTitle!] === optionValue.value;
        })
      );
    },
    [selectedOptions, variants, options]
  );

  // New function to check if this is a first-level option (assumes first option is first-level)
  const isFirstLevelOption = (optionTitle: string) => {
    return options.length > 0 && options[0].title === optionTitle;
  };

  // New function to get first valid second-level option for a given first-level selection
  const getFirstValidSecondLevelOption = useCallback(
    (firstLevelValue: string) => {
      if (options.length < 2) return null;

      const firstOption = options[0];
      const secondOption = options[1];

      // Find all variants that have the selected first-level value
      const validVariants = variants.filter((variant) =>
        variant.option_values.some(
          (opt) =>
            opt.value === firstLevelValue &&
            options.find((o) => o.id == opt.option)?.title === firstOption.title
        )
      );

      // Get all possible second-level values for these variants
      const validSecondLevelValues = validVariants
        .map(
          (variant) =>
            variant.option_values.find(
              (opt) =>
                options.find((o) => o.id == opt.option)?.title ===
                secondOption.title
            )?.value
        )
        .filter(Boolean) as string[];

      // Sort and return the first valid option
      const sortedValues = uniqueOptions(
        validSecondLevelValues,
        secondOption.title
      );
      return sortedValues.length > 0 ? sortedValues[0] : null;
    },
    [variants, options]
  );

  // Function to generate tooltip message for unavailable options
  const getTooltipMessage = (optionTitle: string, value: string) => {
    // Find the other selected option that's causing the conflict
    const otherOptions = options.filter((opt) => opt.title !== optionTitle);

    for (const otherOption of otherOptions) {
      const otherSelectedValue = selectedOptions[otherOption.title];
      if (otherSelectedValue) {
        // Check if this combination would be invalid
        const testSelection = {
          ...selectedOptions,
          [optionTitle]: value,
        };

        const isInvalid = !variants.some((variant) =>
          variant.option_values.every((optionValue) => {
            const optTitle = options.find(
              (opt) => opt.id == optionValue.option
            )?.title;
            return testSelection[optTitle!] === optionValue.value;
          })
        );

        if (isInvalid) {
          return `This option is not available for ${otherOption.title}: ${otherSelectedValue}`;
        }
      }
    }

    return 'This option is not available';
  };

  const uniqueOptions = (values: string[], title?: string) => {
    // Remove duplicates
    const uniqueValues = Array.from(new Set(values));

    // Sort using the provided variants array
    const variantsSortMap = new Map(
      variantOrders
        ?.filter((v) => v?.variant_title) // Ensure `variant_title` is valid
        .map((v) => [v.variant_title.toLowerCase(), v.sort])
    );

    return uniqueValues.sort((a, b) => {
      const sortA = variantsSortMap.get(a.trim().toLowerCase()) || Infinity; // Default to Infinity if not found
      const sortB = variantsSortMap.get(b.trim().toLowerCase()) || Infinity;

      return sortA - sortB; // Sort by the `sort` number
    });
  };

  // Modified handleOptionSelect function
  const handleOptionSelect = (optionTitle: string, value: string) => {
    const isFirstLevel = isFirstLevelOption(optionTitle);

    if (isFirstLevel) {
      // For first-level options, always allow selection
      onSelect(optionTitle, value);

      // Check if current second-level selection is still valid
      if (options.length > 1) {
        const secondOption = options[1];
        const currentSecondLevelValue = selectedOptions[secondOption.title];

        // Check if the current combination is valid
        const isCurrentCombinationValid = variants.some((variant) =>
          variant.option_values.every((optionValue) => {
            const optTitle = options.find(
              (opt) => opt.id == optionValue.option
            )?.title;
            if (optTitle === optionTitle) return optionValue.value === value;
            if (optTitle === secondOption.title)
              return optionValue.value === currentSecondLevelValue;
            return true;
          })
        );

        // If current combination is not valid, auto-select first valid second-level option
        if (!isCurrentCombinationValid) {
          const firstValidSecondLevel = getFirstValidSecondLevelOption(value);
          if (firstValidSecondLevel) {
            onSelect(secondOption.title, firstValidSecondLevel);
          }
        }
      }
    } else {
      // For second-level options, use original logic (only select if available)
      const isAvailable = isOptionCombinationAvailable(optionTitle, value);
      if (isAvailable) {
        onSelect(optionTitle, value);
      }
    }
  };

  useEffect(() => {
    const initializeOptions = () => {
      // Check if options are already selected from URL params
      const hasValidSelections =
        Object.keys(selectedOptions).length > 0 &&
        Object.entries(selectedOptions).every(([optionTitle, value]) => {
          if (!value) return false;

          // Check if this option title exists
          const option = options.find((opt) => opt.title === optionTitle);
          if (!option) return false;

          // Check if this value exists for this option
          const valueExists = option.values.includes(value);
          if (!valueExists) return false;

          // Check if this combination is actually available in variants
          return isOptionCombinationAvailable(optionTitle, value);
        });

      if (hasValidSelections) {
        return;
      }

      let initialSelections: SelectedOptions = {};
      const hash = window?.location?.hash;
      const sku = hash?.replace('#sku=', '');

      if (sku) {
        // Preserve SKU selection logic
        const selectedVariant = variants?.find(
          (variant) => variant?.sku === sku
        );
        if (selectedVariant) {
          // For SKU-based selection, get ALL option values from the matched variant
          selectedVariant.option_values.forEach((optionValue) => {
            const option = options.find((opt) => opt.id == optionValue.option);
            if (option) {
              initialSelections[option.title] = optionValue.value;
            }
          });

          // Apply all selections at once for SKU-based selection
          Object.entries(initialSelections).forEach(([key, value]) => {
            onSelect(key, value as string);
          });

          return; // Exit early since we have a complete SKU match
        }
      }

      // If no SKU match or SKU is not provided, select the first available option dynamically
      if (Object.keys(initialSelections).length === 0 && options.length > 0) {
        const firstOption = options[0]; // Dynamically get the first attribute
        const availableFirstOptionValues =
          availableOptions[firstOption.title] || [];
        const sortedFirstOptionValues = uniqueOptions(
          Array.from(availableFirstOptionValues),
          firstOption.title
        );
        const firstOptionValue = sortedFirstOptionValues[0]; // Pick from sorted order

        if (firstOptionValue) {
          initialSelections[firstOption.title] = firstOptionValue;
        }
      }

      // Select the first valid second option based on the first selection
      if (Object.keys(initialSelections).length > 0 && options.length > 1) {
        const secondOption = options[1]; // Dynamically get the second attribute

        // Get valid variants matching the selected first option
        const validVariants = variants.filter((variant) =>
          variant.option_values.some(
            (opt) =>
              opt.value === initialSelections[options[0].title] &&
              options.find((o) => o.id == opt.option)?.title ===
                options[0].title
          )
        );

        // Find all possible values for the second option in sorted order
        const validSecondOptionValues = validVariants
          .map(
            (variant) =>
              variant.option_values.find(
                (opt) =>
                  options.find((o) => o.id == opt.option)?.title ===
                  secondOption.title
              )?.value
          )
          .filter(Boolean);

        const sortedSecondOptionValues = uniqueOptions(
          validSecondOptionValues as string[],
          secondOption.title
        );
        const firstValidSecondOption = sortedSecondOptionValues[0]; // Pick from sorted order

        if (firstValidSecondOption) {
          initialSelections[secondOption.title] = firstValidSecondOption;
        }
      }

      // Apply the selections
      Object.entries(initialSelections).forEach(([key, value]) => {
        onSelect(key, value as string);
      });
    };

    initializeOptions();
  }, []);

  return (
    <div className="flex flex-col gap-5 py-5 border-t border-border-gray">
      {options?.map((option, labelIndex) => (
        <div key={option.id} className="option-group flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <p className="text-[16px] font-semibold">{option.title}:</p>
            {sizeModal && labelIndex === 0 && (
              <p
                onClick={() => {
                  setIsOpen(true);
                  toggleSizeModal(true);
                }}
                className="flex items-center gap-1 text-xs md:text-base font-proxima_font text-black hover:text-black/70 transition-colors duration-150 cursor-pointer underline"
              >
                <Ruler size={20} className="text-black" />
                Size Guide
              </p>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-3">
            {uniqueOptions(option.values, option.title)?.map((value) => {
              const isActive = selectedOptions[option.title] === value;
              const isOptionAvailable = isOptionCombinationAvailable(
                option.title,
                value
              );
              const isFirstLevel = isFirstLevelOption(option.title);
              const isDisabled = !isFirstLevel && !isOptionAvailable;

              return (
                <VariantButton
                  key={value}
                  label={value}
                  isSelected={isActive}
                  disabled={isDisabled}
                  tooltipMessage={
                    isDisabled
                      ? getTooltipMessage(option.title, value)
                      : undefined
                  }
                  className="text-xs lg:text-sm"
                  onClick={() => {
                    if (
                      selectedOptions[option.title] !== value &&
                      !isDisabled
                    ) {
                      handleOptionSelect(option.title, value);
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
      ))}
      {sizeModal && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            toggleSizeModal(false);
          }}
          title={sizeModal?.heading}
          className="z-50"
        >
          <p className="text-xs md:text-sm lg:text-base">
            {sizeModal.description}
          </p>
          <div
            dangerouslySetInnerHTML={{ __html: sizeModal.guide }}
            className="overflow-x-auto bg-black rounded-md mt-8"
          />
        </Modal>
      )}
    </div>
  );
};

export default Options;
